import { observer } from "mobx-react-lite";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { Button, Dropdown } from "antd";
import { EasyAvatar } from "fin-web-components";
import { globalModel } from "../../global/GlobalModel";
import { chineseWordmarkSrc } from "./chineseWordmark";
import {
  useRelatedSpacesOrdersModals,
} from "./RelatedSpacesOrdersModalsProvider";
import { shellUserDropdownMenu } from "./shellUserDropdownMenu";
import { IS_STANDALONE } from "../../standalone/config";
import { siteConfig } from "../../site-config/SiteConfig";
import { siteNavUserAvatarCssVars, siteNavUserAvatarSize } from "../../SiteConfig";
import "./ShellTopBar.css";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";
import { resolveAppMediaUrl } from "../../utils/resolveAppMediaUrl";

type Props = {
  onLoginClick?: () => void;
  /** 首页全屏图：顶栏白字、透明底 */
  variant?: "default" | "hero";
};

/** 顶栏最多直接展示的空间数，其余从「我的关注」进入 */
const NAV_INLINE_SPACE_MAX = 3;

export const ShellTopBar = observer((props: Props) => {
  const { variant = "default" } = props;
  const navigate = useNavigate();
  const modals = useRelatedSpacesOrdersModals();
  const { pathname } = useLocation();
  const u = globalModel.currentUser;
  const av = resolveAppMediaUrl(u?.avatarUrl);
  const shellClass =
    variant === "hero" ? "shell-top shell-top--hero" : "shell-top";

  const ordered = globalModel.recentSpaceEntries;
  const recentChips =
    ordered.length === 0
      ? []
      : ordered.length <= NAV_INLINE_SPACE_MAX
        ? ordered.map((x) => ({
            kind: "space" as const,
            id: x.id,
            name: x.name,
          }))
        : [
            ...ordered
              .slice(0, NAV_INLINE_SPACE_MAX)
              .map((x) => ({
                kind: "space" as const,
                id: x.id,
                name: x.name,
              })),
            { kind: "subs" as const },
          ];

  /** 无最近访问空间时仍露出「与我相关的空间」入口，避免顶栏一片空白 */
  const navChips =
    recentChips.length > 0
      ? recentChips
      : ([
          { kind: "lobby_follow" as const },
          { kind: "lobby_sub" as const },
        ] as const);

  const spaceMatch = matchPath({ path: "/space/:spaceId/*" }, pathname);
  const currentSpaceId = spaceMatch?.params?.spaceId;

  const userMenu = shellUserDropdownMenu({
    navigate,
    onFollowsClick: () => modals.openRelatedSpaces({ initialTab: "follow" }),
    onOrdersClick: () => modals.openOrdersSpace(),
  });

  const inner = (
    <>
      <div
        className="shell-brand"
        onClick={() => navigate("/")}
        role="presentation"
      >
        <img
          className="shell-brand-img"
          src={siteConfig.shellLogo}
          alt={siteConfig.shellLogoAlt}
          draggable={false}
        />
        {siteConfig.showChineseWordmark && (
          <img
            className="shell-brand-wordmark"
            src={chineseWordmarkSrc(variant === "hero" ? "dark" : "light")}
            alt=""
            draggable={false}
          />
        )}
        {siteConfig.shellTagline ? (
          <span
            className={
              variant === "hero"
                ? "shell-brand-tagline shell-brand-tagline--on-dark"
                : "shell-brand-tagline"
            }
            style={{ paddingTop: "2px" }}
          >
            {siteConfig.shellTagline}
          </span>
        ) : null}
      </div>
      <div className="shell-right">
        {globalModel.isLoggedIn && (
          <div
            className={
              variant === "hero"
                ? "shell-recent shell-recent--on-dark"
                : "shell-recent"
            }
          >
            <nav
              className="shell-recent-nav"
              aria-label={
                recentChips.length > 0
                  ? "可访问空间，按最近访问排序；超出部分在「我的订阅」中"
                  : "与我相关的空间"
              }
            >
              {navChips.map((c) => {
                if (c.kind === "space") {
                  const active = currentSpaceId === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className={
                        active
                          ? "shell-recent-link shell-recent-link--active"
                          : "shell-recent-link"
                      }
                      onClick={() => navigate(`/space/${c.id}/home`)}
                    >
                      {c.name}
                    </button>
                  );
                }
                if (c.kind === "lobby_follow") {
                  const followActive =
                    modals.relatedModalOpen &&
                    modals.relatedModalTab === "follow";
                  return (
                    <button
                      key="shell-nav-follow"
                      type="button"
                      className={
                        followActive
                          ? "shell-recent-link shell-recent-link--active"
                          : "shell-recent-link"
                      }
                      onClick={() =>
                        modals.openRelatedSpaces({ initialTab: "follow" })
                      }
                    >
                      我的关注
                    </button>
                  );
                }
                if (c.kind === "lobby_sub") {
                  const subsActive =
                    modals.relatedModalOpen && modals.relatedModalTab === "sub";
                  return (
                    <button
                      key="shell-nav-subs-only"
                      type="button"
                      className={[
                        "shell-recent-link",
                        "shell-recent-item--subs",
                        subsActive ? "shell-recent-link--active" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() =>
                        modals.openRelatedSpaces({ initialTab: "sub" })
                      }
                    >
                      我的订阅
                    </button>
                  );
                }
                const subsActive =
                  modals.relatedModalOpen && modals.relatedModalTab === "sub";
                return (
                  <button
                    key="shell-nav-subs"
                    type="button"
                    className={[
                      "shell-recent-link",
                      "shell-recent-item--subs",
                      subsActive ? "shell-recent-link--active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() =>
                      modals.openRelatedSpaces({ initialTab: "sub" })
                    }
                  >
                    我的订阅
                  </button>
                  );
              })}
            </nav>
          </div>
        )}
        {globalModel.isLoggedIn ? (
          <Dropdown
            menu={userMenu}
            trigger={["click"]}
            getPopupContainer={() => document.body}
            overlayStyle={{ zIndex: 2000 }}
          >
            <div
              className={
                variant === "hero" ? "shell-user shell-user--on-dark" : "shell-user"
              }
            >
              <EasyAvatar
                shape="circle"
                size={siteNavUserAvatarSize}
                url={av || undefined}
                word={avatarDisplayWord(u?.name ?? "")}
                alt=""
                className="shell-user-ea"
              />
              <span className="shell-user-name">
                {u?.name ?? "用户"}
              </span>
            </div>
          </Dropdown>
        ) : (
          <>
            {!IS_STANDALONE && (
              <Button
                type="text"
                className={
                  variant === "hero" ? "shell-dav shell-dav--on-dark" : "shell-dav"
                }
                onClick={() => navigate("/creator-apply")}
              >
                我是大V
              </Button>
            )}
            <Button
              type="text"
              className={
                variant === "hero" ? "shell-login shell-login--on-dark" : "shell-login"
              }
              onClick={props.onLoginClick}
            >
              登录/注册
            </Button>
          </>
        )}
      </div>
    </>
  );

  return (
    <header className={shellClass} style={siteNavUserAvatarCssVars()}>
      <div className="shell-top-inner">{inner}</div>
    </header>
  );
});
